import { useEffect, useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Progress } from "@/components/ui/progress"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from '@/lib/supabase'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { 
    ChevronDown, 
    ChevronRight, 
    Clock, 
    FileText, 
    Share, 
    AlertTriangle,
    CheckCircle,
    XCircle,
    Play,
    Search,
    Filter,
    RefreshCw,
    Calendar,
    BarChart3,
    Database,
    Copy,
    Zap,
    ExternalLink,
    Mail,
    CalendarClock,
    Share2,
    Bot,
    Workflow,
    Layers3,
    Send,
    Settings,
    BookOpen
} from 'lucide-react'
import { format, formatDistanceToNow, subDays, subHours, subMinutes, startOfDay } from 'date-fns'
import { useRole } from '@/lib/useRole'
import { useBrands } from '@/lib/BrandsProvider'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"

// Task type constants for manual triggers
const TASK_TYPES = {
    CHECK_INACTIVE_BILLING: 'check_inactive_billing',
    PROCESS_BRAND_CONVERGENCES: 'process_brand_convergences',
    PROCESS_ALL: 'process_all',
    WEEKLY_REVIEW: 'weekly_review',
    AUTOPOST_CONTENT_GENERATION: 'autopost_content_generation',
    AUTOPOST_PUBLISHING: 'autopost_publishing',
    DAILY_CONTENT_DIGEST: 'daily_content_digest',
    FETCH_POST_STATS: 'fetch_post_stats',
}

// Clean TypeScript interfaces for cron log data
// NO MORE PARSING NIGHTMARE - Direct object access
interface CleanCronExecutionInfo {
    start_time: string
    end_time?: string
    duration_ms?: number
    status: 'started' | 'completed' | 'failed' | 'cancelled'
    cron_schedule?: string
    trigger_type?: 'scheduled' | 'manual' | 'queue'
    triggered_by?: string
}

interface CleanWorkflowStep {
    step: string
    timestamp: string
    duration_ms?: number
    status?: 'started' | 'completed' | 'failed' | 'skipped'
    result?: Record<string, any>
    error_message?: string
}

interface CleanContentGenerated {
    content_id: string
    content_type: string
    brand_id: string
    convergence_id?: string
    name?: string
    created_at: string
}

interface CleanContentPublished {
    post_id: string
    content_id?: string
    platform: string
    brand_id: string
    status: 'published' | 'failed' | 'scheduled' | 'pending'
    published_at?: string
    scheduled_at?: string
    error_message?: string
}

interface CleanQueueMessage {
    message_id: string
    queue_name: string
    timestamp: string
    status?: 'sent' | 'processed' | 'failed'
    processing_time_ms?: number
}

interface CleanPerformanceMetrics {
    items_processed?: number
    success_count?: number
    error_count?: number
    brands_processed?: number
    emails_sent?: number
    convergences_created?: number
    content_generated?: number
    posts_published?: number
    queue_messages_sent?: number
    database_operations?: number
    api_calls?: number
    credit_consumption?: number
}

interface CleanErrorInfo {
    message: string
    stack?: string
    error_code?: string
    context?: Record<string, any>
    retry_count?: number
    is_retryable?: boolean
}

// Main clean cron log details interface
interface CleanCronLogDetails {
    execution: CleanCronExecutionInfo
    workflow_steps?: CleanWorkflowStep[]
    content_generated?: CleanContentGenerated[]
    content_published?: CleanContentPublished[]
    queue_messages?: CleanQueueMessage[]
    metrics?: CleanPerformanceMetrics
    error?: CleanErrorInfo
    custom?: Record<string, any>
    _meta?: {
        version: string
        created_by: string
        schema_version: '2.0'
    }
}

// Main cron job log type
interface CleanCronJobLog {
    id: string | null
    job_name: string | null
    status: string | null
    run_at: string | null
    execution_time: unknown | null
    error_message: string | null
    details: any | null  // Can be Json or CleanCronLogDetails
    brand_id: string | null
    parent_log_id: string | null
    created_at: string | null
    structure_type?: string | null
    children?: CleanCronJobLog[]
    // Additional fields from cron_logs_clean_detailed view
    content_generated_count?: number | null
    content_published_count?: number | null
    execution_status?: string | null
    items_processed?: number | null
    structured_error_message?: string | null
    workflow_steps_count?: number | null
}



// Type guard functions
function isCleanCronLogDetails(details: any): details is CleanCronLogDetails {
    return details && 
           typeof details === 'object' && 
           details.execution && 
           typeof details.execution === 'object' &&
           details._meta?.schema_version === '2.0'
}

// Helper functions for UI
const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
        case 'success':
        case 'completed':
            return <CheckCircle className="w-4 h-4 text-green-600" />
        case 'error':
        case 'failed':
            return <XCircle className="w-4 h-4 text-red-600" />
        case 'running':
        case 'in_progress':
        case 'started':
            return <Play className="w-4 h-4 text-blue-600" />
        default:
            return <Clock className="w-4 h-4 text-gray-600" />
    }
}

// Get appropriate icon for each job type
const getJobTypeIcon = (jobName: string) => {
    const name = jobName?.toLowerCase() || ''
    
    if (name.includes('autopost_content_generation')) {
        return <Bot className="w-4 h-4 text-purple-600" />
    } else if (name.includes('autopost_content_queued')) {
        return <Send className="w-4 h-4 text-blue-600" />
    } else if (name.includes('brand_convergence_queued')) {
        return <Workflow className="w-4 h-4 text-green-600" />
    } else if (name.includes('daily_content_digest')) {
        return <Mail className="w-4 h-4 text-orange-600" />
    } else if (name.includes('manual_trigger_process_all')) {
        return <Layers3 className="w-4 h-4 text-indigo-600" />
    } else if (name.includes('scheduled_brand_processing')) {
        return <Workflow className="w-4 h-4 text-green-600" />
    } else if (name.includes('scheduled_post_sender')) {
        return <Share2 className="w-4 h-4 text-teal-600" />
    } else if (name.includes('check_inactive_billing')) {
        return <CheckCircle className="w-4 h-4 text-green-600" />
    } else if (name.includes('process_brand_convergences')) {
        return <RefreshCw className="w-4 h-4 text-blue-600" />
    } else if (name.includes('process_all')) {
        return <CalendarClock className="w-4 h-4 text-purple-600" />
    } else if (name.includes('weekly_review')) {
        return <Mail className="w-4 h-4 text-orange-600" />
    } else if (name.includes('fetch_post_stats')) {
        return <BarChart3 className="w-4 h-4 text-cyan-600" />
    } else {
        return <Settings className="w-4 h-4 text-gray-600" />
    }
}

// Get file references for each job type
const getJobFileReferences = (jobName: string): { label: string; files: string[] } => {
    const name = jobName?.toLowerCase() || ''
    
    if (name.includes('autopost_content_generation')) {
        return {
            label: 'Content Generation Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/scheduled-content-generation-worker.ts',
                'workers/src/rainfactory/content_and_convergences/content-generator-worker.ts',
                'workers/src/_shared/services/AutopostDecisionService.ts',
                'workers/src/rainfactory/content_and_convergences/shared-content-creation.ts'
            ]
        }
    } else if (name.includes('autopost_content_queued') || name.includes('autopost_publishing')) {
        return {
            label: 'Content Publishing Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/scheduled-post-sender.ts',
                'workers/src/rainfactory/content_and_convergences/social-post-publisher.ts',
                'workers/src/rainfactory/social-platforms/platform-adapter.ts',
                'workers/src/rainfactory/autopost/social-post-queue-worker.ts'
            ]
        }
    } else if (name.includes('brand_convergence_queued') || name.includes('scheduled_brand_processing')) {
        return {
            label: 'Brand Processing Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/scheduled-brand-processing.ts',
                'workers/src/rainfactory/content_and_convergences/convergence-task-worker.ts',
                'workers/src/rainfactory/content_and_convergences/lib/database-service.ts'
            ]
        }
    } else if (name.includes('daily_content_digest')) {
        return {
            label: 'Email Digest Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/daily-content-digest-worker.ts',
                'workers/src/rainfactory/trigger-daily-content-digest.ts',
                'workers/src/_shared/services/emailSender.ts',
                'workers/src/_shared/services/emailBuilder.ts'
            ]
        }
    } else if (name.includes('manual_trigger_process_all')) {
        return {
            label: 'Process All Files',
            files: [
                'workers/src/rainfactory/trigger-scheduled-task.ts',
                'workers/src/routes/rainfactory.ts',
                'workers/src/rainfactory/content_and_convergences/scheduled-brand-processing.ts'
            ]
        }
    } else if (name.includes('scheduled_post_sender')) {
        return {
            label: 'Post Sender Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/scheduled-post-sender.ts',
                'workers/src/rainfactory/content_and_convergences/social-post-publisher.ts',
                'workers/src/rainfactory/social-platforms/platform-adapter.ts'
            ]
        }
    } else if (name.includes('check_inactive_billing')) {
        return {
            label: 'Billing Check Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/scheduled-brand-processing.ts',
                'workers/src/billing-functions/BillingHttpWorker.ts',
                'workers/src/billing-functions/billing-webhooks-worker.ts'
            ]
        }
    } else if (name.includes('process_brand_convergences')) {
        return {
            label: 'Brand Convergence Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/scheduled-brand-processing.ts',
                'workers/src/rainfactory/content_and_convergences/convergence-task-worker.ts',
                'workers/src/rainfactory/content_and_convergences/lib/database-service.ts'
            ]
        }
    } else if (name.includes('weekly_review')) {
        return {
            label: 'Weekly Review Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/weekly-review-worker.ts',
                'workers/src/rainfactory/trigger-weekly-review.ts',
                'workers/src/_shared/services/emailSender.ts'
            ]
        }
    } else if (name.includes('fetch_post_stats')) {
        return {
            label: 'Post Statistics Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/fetch-post-stats-worker.ts',
                'workers/src/rainfactory/social-platforms/instagram-adapter.ts',
                'workers/src/rainfactory/social-platforms/facebook-adapter.ts',
                'workers/src/rainfactory/social-platforms/platform-adapter.ts'
            ]
        }
    } else {
        return {
            label: 'General Task Files',
            files: [
                'workers/src/rainfactory/content_and_convergences/scheduled-brand-processing.ts',
                'workers/src/routes/rainfactory.ts'
            ]
        }
    }
}

const getStatusBadgeVariant = (status: string): "default" | "destructive" | "secondary" | "outline" => {
    switch (status.toLowerCase()) {
        case 'success':
        case 'completed':
            return 'default'
        case 'error':
        case 'failed':
            return 'destructive'
        case 'running':
        case 'in_progress':
        case 'started':
            return 'secondary'
        default:
            return 'outline'
    }
}


const formatExecutionTime = (executionTime: unknown): string => {
    if (!executionTime) return 'N/A'
    if (typeof executionTime === 'string') {
        const parts = executionTime.split(':')
        if (parts.length === 3) {
            const hours = parseInt(parts[0])
            const minutes = parseInt(parts[1])
            const seconds = parseFloat(parts[2])
            const totalMs = (hours * 3600 + minutes * 60 + seconds) * 1000
            return `${totalMs.toFixed(0)}ms`
        }
        return executionTime
    }
    return String(executionTime)
}

// Manual Triggers Toolbar Component
function ManualTriggersToolbar({ 
    triggerTask, 
    triggerLoading, 
    selectedBrand 
}: {
    triggerTask: (taskName: string) => Promise<void>
    triggerLoading: { [key: string]: boolean }
    selectedBrand: any
}) {
    const triggers = [
        {
            key: TASK_TYPES.CHECK_INACTIVE_BILLING,
            icon: <CheckCircle className="w-4 h-4" />,
            tooltip: "Check Inactive Billing",
            description: "Check for inactive billing accounts and disable their brands",
            variant: "outline" as const
        },
        {
            key: TASK_TYPES.PROCESS_BRAND_CONVERGENCES,
            icon: <RefreshCw className="w-4 h-4" />,
            tooltip: "Find & Re-rank Convergences (Selected Brand)",
            description: selectedBrand ? `Process convergences for ${selectedBrand.brand_name}` : "Select a brand to process convergences",
            variant: "outline" as const,
            disabled: !selectedBrand
        },
        {
            key: TASK_TYPES.PROCESS_ALL,
            icon: <CalendarClock className="w-4 h-4" />,
            tooltip: "Process All",
            description: "Run the complete scheduled task process for all brands",
            variant: "secondary" as const
        },
        {
            key: TASK_TYPES.WEEKLY_REVIEW,
            icon: <Mail className="w-4 h-4" />,
            tooltip: "Send Weekly Review",
            description: "Generate and send weekly review emails to all active brands",
            variant: "outline" as const
        },
        {
            key: TASK_TYPES.AUTOPOST_CONTENT_GENERATION,
            icon: <Bot className="w-4 h-4" />,
            tooltip: "Test Auto-Creation",
            description: "Trigger daily content generation cron job",
            variant: "outline" as const
        },
        {
            key: TASK_TYPES.AUTOPOST_PUBLISHING,
            icon: <Share2 className="w-4 h-4" />,
            tooltip: "Test Publishing",
            description: "Trigger 5-minute publishing check cron job",
            variant: "outline" as const
        },
        {
            key: TASK_TYPES.DAILY_CONTENT_DIGEST,
            icon: <FileText className="w-4 h-4" />,
            tooltip: "Daily Content Digest",
            description: "Send daily digest emails with content updates",
            variant: "outline" as const
        },
        {
            key: TASK_TYPES.FETCH_POST_STATS,
            icon: <BarChart3 className="w-4 h-4" />,
            tooltip: "Fetch Post Statistics",
            description: "Fetch statistics for Instagram and Facebook posts from last 30 days",
            variant: "outline" as const
        }
    ]

    return (
        <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
                <div className="flex flex-col space-y-3">
                    <div className="flex items-center space-x-2">
                        <Settings className="w-4 h-4 text-blue-600" />
                        <h3 className="font-medium text-blue-900">Manual Triggers</h3>
                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Superadmin Only</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                        {triggers.map((trigger) => (
                            <Tooltip key={trigger.key}>
                                <TooltipTrigger asChild>
                                    <Button
                                        onClick={() => triggerTask(trigger.key)}
                                        disabled={triggerLoading[trigger.key] || trigger.disabled}
                                        variant={trigger.variant}
                                        size="sm"
                                        className="h-9 px-3"
                                    >
                                        {triggerLoading[trigger.key] ? (
                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                        ) : (
                                            trigger.icon
                                        )}
                                        <span className="ml-2 hidden sm:inline">
                                            {trigger.tooltip}
                                        </span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                    <div className="space-y-1">
                                        <p className="font-medium">{trigger.tooltip}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {trigger.description}
                                        </p>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default function CronLogsCleanFrontend() {
    const { toast } = useToast()
    const { isSuperAdmin } = useRole()
    const { selectedBrand } = useBrands()
    const [logs, setLogs] = useState<CleanCronJobLog[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [jobNameFilter, setJobNameFilter] = useState('')
    const [dateRange, setDateRange] = useState<'5m' | '1h' | '1d' | '3d' | '7d' | '30d' | 'all'>('7d')
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [triggerLoading, setTriggerLoading] = useState<{ [key: string]: boolean }>({})

    const fetchLogs = useCallback(async () => {
        try {
            let query = supabase
                .from('cron_logs_clean_detailed')
                .select('*')
                .order('created_at', { ascending: false })

            // Apply date range filter
            if (dateRange !== 'all') {
                let cutoffDate: Date
                const now = new Date()
                
                if (dateRange === '5m') {
                    cutoffDate = subMinutes(now, 5)
                } else if (dateRange === '1h') {
                    cutoffDate = subHours(now, 1)
                } else {
                    const days = parseInt(dateRange.replace('d', ''))
                    cutoffDate = startOfDay(subDays(now, days))
                }
                
                query = query.gte('created_at', cutoffDate.toISOString())
            }

            // Apply filters
            if (jobNameFilter) {
                query = query.ilike('job_name', `%${jobNameFilter}%`)
            }

            if (statusFilter) {
                query = query.eq('status', statusFilter)
            }


            query = query.limit(100)

            const { data, error } = await query

            if (error) throw error
            
            setLogs(data || [])
        } catch (error) {
            console.error('Error fetching clean cron logs:', error)
            toast({
                title: "Error fetching logs",
                description: "Failed to fetch cron logs",
                variant: "destructive"
            })
        } finally {
            setLoading(false)
        }
    }, [dateRange, jobNameFilter, statusFilter, toast])

    useEffect(() => {
        fetchLogs()
    }, [fetchLogs])

    // Auto-refresh functionality
    useEffect(() => {
        let interval: NodeJS.Timeout
        
        if (autoRefresh) {
            interval = setInterval(() => {
                fetchLogs()
            }, 10000)
        }
        
        return () => {
            if (interval) {
                clearInterval(interval)
            }
        }
    }, [autoRefresh, fetchLogs])

    // Get unique job names for filter dropdown
    const uniqueJobNames = useMemo(() => {
        const names = [...new Set(logs.map(log => log.job_name).filter(Boolean) as string[])]
        return names.sort()
    }, [logs])

    // Organize logs into hierarchical structure
    const organizedLogs = useMemo(() => {
        const parentLogs = logs.filter(log => !log.parent_log_id)
        const childLogs = logs.filter(log => log.parent_log_id)
        
        return parentLogs.map(parent => ({
            ...parent,
            children: childLogs.filter(child => child.parent_log_id === parent.id)
        }))
    }, [logs])

    // Filter logs based on search
    const filteredLogs = useMemo(() => {
        return organizedLogs.filter(log => {
            if (!searchTerm) return true

            const searchLower = searchTerm.toLowerCase()

            // Search in basic fields
            if (log.job_name?.toLowerCase().includes(searchLower)) return true
            if (log.error_message?.toLowerCase().includes(searchLower)) return true

            // Search in clean details structure
            const cleanDetails = isCleanCronLogDetails(log.details) ? log.details : null
            if (cleanDetails) {
                // Search in workflow steps
                if (cleanDetails.workflow_steps?.some(step =>
                    step.step.toLowerCase().includes(searchLower) ||
                    step.error_message?.toLowerCase().includes(searchLower) ||
                    JSON.stringify(step.result).toLowerCase().includes(searchLower)
                )) return true

                // Search in content generated
                if (cleanDetails.content_generated?.some(content =>
                    content.content_type.toLowerCase().includes(searchLower) ||
                    content.name?.toLowerCase().includes(searchLower) ||
                    content.content_id.toLowerCase().includes(searchLower)
                )) return true

                // Search in content published
                if (cleanDetails.content_published?.some(post =>
                    post.platform.toLowerCase().includes(searchLower) ||
                    post.post_id.toLowerCase().includes(searchLower) ||
                    post.error_message?.toLowerCase().includes(searchLower)
                )) return true

                // Search in error details
                if (cleanDetails.error?.message.toLowerCase().includes(searchLower)) return true
                if (cleanDetails.error?.error_code?.toLowerCase().includes(searchLower)) return true
            }

            return false
        })
    }, [organizedLogs, searchTerm])

    const toggleRowExpansion = (id: string) => {
        const newExpanded = new Set(expandedRows)
        if (newExpanded.has(id)) {
            newExpanded.delete(id)
        } else {
            newExpanded.add(id)
        }
        setExpandedRows(newExpanded)
    }

    const refreshLogs = async () => {
        setLoading(true)
        await fetchLogs()
    }

    // Manual trigger functionality (similar to ScheduledTasks)
    const triggerTask = async (taskName: string) => {
        if (taskName === TASK_TYPES.PROCESS_BRAND_CONVERGENCES && !selectedBrand) {
            toast({
                title: "No brand selected",
                description: "Please select a brand to process convergences.",
                variant: "destructive",
            })
            return
        }
        
        if (!isSuperAdmin) {
            toast({
                title: "Unauthorized",
                description: "Only superadmins can execute scheduled operations.",
                variant: "destructive",
            })
            return
        }

        setTriggerLoading(prev => ({ ...prev, [taskName]: true }))
        
        try {
            const session = await supabase.auth.getSession()
            if (!session.data.session) {
                throw new Error('No session found')
            }

            const workerUrl = import.meta.env.VITE_WORKERS_URL
            let endpoint: string
            let payload: any

            if (taskName === TASK_TYPES.DAILY_CONTENT_DIGEST) {
                endpoint = `${workerUrl}/rainfactory/trigger-daily-content-digest`
                payload = {}
            } else if (taskName === TASK_TYPES.FETCH_POST_STATS) {
                endpoint = `${workerUrl}/rainfactory/trigger-fetch-post-stats`
                payload = {}
            } else {
                endpoint = `${workerUrl}/rainfactory/trigger-scheduled-task`
                payload = { taskName }

                if (taskName === TASK_TYPES.PROCESS_BRAND_CONVERGENCES && selectedBrand) {
                    payload.brandId = selectedBrand.id
                }
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.data.session.access_token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data?.error || `HTTP error! status: ${response.status}`)
            }

            toast({
                title: "Task triggered successfully",
                description: data.message,
            })
            
            // Refresh logs after triggering task
            await fetchLogs()
        } catch (error) {
            console.error('Error triggering task:', error)
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            
            toast({
                title: "Error triggering task",
                description: errorMessage,
                variant: "destructive",
            })
        } finally {
            setTriggerLoading(prev => ({ ...prev, [taskName]: false }))
        }
    }

    // Export function that creates clean, structured JSON
    const exportLogs = async () => {
        try {
            const dataToExport = {
                export_metadata: {
                    export_date: new Date().toISOString(),
                    export_type: "clean_cron_logs",
                    total_logs: filteredLogs.length,
                    filters_applied: {
                        search_term: searchTerm || null,
                        status_filter: statusFilter || null,
                        job_name_filter: jobNameFilter || null,
                        date_range: dateRange
                    },
                    version: "2.0"
                },
                logs: filteredLogs.map(log => ({
                    ...log,
                    // Direct access - no parsing needed!
                    clean_structure_summary: isCleanCronLogDetails(log.details) ? {
                        execution_status: log.details.execution.status,
                        workflow_steps_count: log.details.workflow_steps?.length || 0,
                        content_generated_count: log.details.content_generated?.length || 0,
                        content_published_count: log.details.content_published?.length || 0,
                        has_error: !!log.details.error,
                        has_metrics: !!log.details.metrics,
                        duration_ms: log.details.execution.duration_ms
                    } : null
                }))
            }

            const jsonString = JSON.stringify(dataToExport, null, 2)
            await navigator.clipboard.writeText(jsonString)

            const totalLogs = dataToExport.logs.length
            const dataSizeKB = Math.round(new Blob([jsonString]).size / 1024)

            toast({
                title: "✅ Clean Data Copied!",
                description: `Successfully copied ${totalLogs} logs (${dataSizeKB} KB) as clean, structured JSON. No parsing required!`,
            })
        } catch (error) {
            console.error('Error copying logs to clipboard:', error)
            toast({
                title: "Copy failed",
                description: "Failed to copy logs to clipboard",
                variant: "destructive"
            })
        }
    }

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="flex items-center space-x-2">
                    <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="h-12 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <TooltipProvider>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">

                    
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            onClick={refreshLogs}
                            variant="outline"
                            size="sm"
                            disabled={loading}
                            title="Refresh log data"
                        >
                            <RefreshCw className={`w-4 h-4 mr-1 ${loading || autoRefresh ? 'animate-spin' : ''}`} />
                            {autoRefresh ? 'Auto' : 'Refresh'}
                        </Button>
                        
                        <Button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            variant={autoRefresh ? "default" : "outline"}
                            size="sm"
                            title={autoRefresh ? "Disable auto-refresh" : "Enable auto-refresh"}
                        >
                            <BarChart3 className="w-4 h-4 mr-1" />
                            Live
                        </Button>
                        
                        <Button
                            onClick={exportLogs}
                            variant="outline"
                            size="sm"
                            disabled={filteredLogs.length === 0}
                            title="Export clean structured data - no parsing required!"
                        >
                            <Copy className="w-4 h-4 mr-1" />
                            Export Clean Data
                        </Button>
                    </div>
                </div>
                
                {/* Manual Triggers Toolbar */}
                {isSuperAdmin && (
                    <ManualTriggersToolbar 
                        triggerTask={triggerTask}
                        triggerLoading={triggerLoading}
                        selectedBrand={selectedBrand}
                    />
                )}
            </div>

            {/* Filter Controls */}
            <div className="flex flex-col space-y-4">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                            placeholder="Search jobs, steps, content, errors..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 w-full"
                        />
                    </div>

                    <Select value={dateRange} onValueChange={(value: any) => setDateRange(value)}>
                        <SelectTrigger className="w-32">
                            <Calendar className="w-4 h-4 mr-2" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="5m">Last 5 Min</SelectItem>
                            <SelectItem value="1h">Last Hour</SelectItem>
                            <SelectItem value="1d">Last Day</SelectItem>
                            <SelectItem value="3d">Last 3 Days</SelectItem>
                            <SelectItem value="7d">Last Week</SelectItem>
                            <SelectItem value="30d">Last Month</SelectItem>
                            <SelectItem value="all">All Time</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
                        <SelectTrigger className="w-32">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="success">Success</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="error">Error</SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                            <SelectItem value="started">Started</SelectItem>
                        </SelectContent>
                    </Select>


                    <Select value={jobNameFilter || "all"} onValueChange={(value) => setJobNameFilter(value === "all" ? "" : value)}>
                        <SelectTrigger className="w-48">
                            <Database className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Job Name" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Jobs</SelectItem>
                            {uniqueJobNames.map(name => (
                                <SelectItem key={name} value={name}>{name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {(statusFilter || jobNameFilter || searchTerm || dateRange !== '7d') && (
                        <Button
                            onClick={() => {
                                setStatusFilter('')
                                setJobNameFilter('')
                                setSearchTerm('')
                                setDateRange('7d')
                            }}
                            variant="ghost"
                            size="sm"
                        >
                            Clear Filters
                        </Button>
                    )}
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-3 md:p-4">
                        <div className="flex items-center space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <div>
                                <p className="text-xs md:text-sm text-muted-foreground">Successful</p>
                                <p className="text-lg md:text-xl font-semibold">
                                    {logs.filter(l => l.status && ['success', 'completed'].includes(l.status)).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-3 md:p-4">
                        <div className="flex items-center space-x-2">
                            <XCircle className="w-5 h-5 text-red-600" />
                            <div>
                                <p className="text-xs md:text-sm text-muted-foreground">Failed</p>
                                <p className="text-lg md:text-xl font-semibold">
                                    {logs.filter(l => l.status && ['error', 'failed'].includes(l.status)).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-3 md:p-4">
                        <div className="flex items-center space-x-2">
                            <Play className="w-5 h-5 text-blue-600" />
                            <div>
                                <p className="text-xs md:text-sm text-muted-foreground">Running</p>
                                <p className="text-lg md:text-xl font-semibold">
                                    {logs.filter(l => l.status && ['running', 'started', 'in_progress'].includes(l.status)).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-3 md:p-4">
                        <div className="flex items-center space-x-2">
                            <Clock className="w-5 h-5 text-gray-600" />
                            <div>
                                <p className="text-xs md:text-sm text-muted-foreground">Total</p>
                                <p className="text-lg md:text-xl font-semibold">{organizedLogs.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Logs Table */}
            <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <Table className="w-full">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8"></TableHead>
                                <TableHead className="w-1/3">Job Name</TableHead>
                                <TableHead className="w-16">Status</TableHead>
                                <TableHead className="w-1/5">Progress</TableHead>
                                <TableHead className="w-20">Duration</TableHead>
                                <TableHead className="w-16">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredLogs.map((log) => (
                                <CleanLogRow 
                                    key={log.id || ''} 
                                    log={log} 
                                    isExpanded={expandedRows.has(log.id || '')}
                                    onToggle={() => toggleRowExpansion(log.id || '')}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {filteredLogs.length === 0 && (
                <div className="text-center py-12">
                    <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No logs found</h3>
                    <p className="text-gray-500">
                        {searchTerm || statusFilter ? 'Try adjusting your filters.' : 'No cron jobs have run recently.'}
                    </p>
                </div>
            )}
        </div>
        </TooltipProvider>
    )
}

// Individual log row component with clean data access
function CleanLogRow({ 
    log, 
    isExpanded, 
    onToggle 
}: { 
    log: CleanCronJobLog & { children?: CleanCronJobLog[] }
    isExpanded: boolean
    onToggle: () => void
}) {
    // Direct access to clean structure - no parsing!
    const cleanDetails = isCleanCronLogDetails(log.details) ? log.details : null
    const hasCleanDetails = !!cleanDetails
    const hasChildren = log.children && log.children.length > 0
    const shouldShowExpansion = hasCleanDetails || hasChildren

    // Calculate progress from clean workflow steps
    const workflowProgress = useMemo(() => {
        if (!cleanDetails?.workflow_steps?.length) return null
        
        const completedSteps = cleanDetails.workflow_steps.filter(
            step => step.status === 'completed'
        ).length
        const totalSteps = cleanDetails.workflow_steps.length
        return Math.round((completedSteps / Math.max(totalSteps, 1)) * 100)
    }, [cleanDetails?.workflow_steps])

    return (
        <>
            <TableRow 
                className={`cursor-pointer hover:bg-gray-50 transition-colors ${log.status === 'error' || log.status === 'failed' ? 'bg-red-50 hover:bg-red-100' : ''}`}
                onClick={shouldShowExpansion ? onToggle : undefined}
            >
                <TableCell>
                    {shouldShowExpansion && (
                        <div className="flex items-center justify-center">
                            {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                        </div>
                    )}
                </TableCell>
                
                <TableCell className="font-medium">
                    <div className="flex items-center space-x-2 min-w-0">
                        {getJobTypeIcon(log.job_name || '')}
                        <div className="flex-1 min-w-0">
                            <div className="truncate">{log.job_name || 'N/A'}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                                <div className="hidden md:block">
                                    {log.run_at ? format(new Date(log.run_at), 'MMM d, HH:mm:ss') : 'N/A'}
                                </div>
                                <div className="md:hidden">
                                    {log.run_at ? format(new Date(log.run_at), 'MMM d HH:mm') : 'N/A'}
                                </div>
                                <div className="text-xs text-muted-foreground hidden sm:block">
                                    {log.run_at ? formatDistanceToNow(new Date(log.run_at), { addSuffix: true }) : 'N/A'}
                                </div>
                            </div>
                        </div>
                        {log.brand_id && (
                            <Badge variant="outline" className="text-xs flex-shrink-0">
                                Brand
                            </Badge>
                        )}
                    </div>
                </TableCell>
                
                <TableCell>
                    <Badge variant={getStatusBadgeVariant(cleanDetails?.execution?.status || log.status || 'completed')}>
                        {cleanDetails?.execution?.status || log.status || 'Completed'}
                    </Badge>
                </TableCell>
                
                <TableCell>
                    <div className="space-y-1">
                        {workflowProgress !== null && (
                            <div className="w-24">
                                <Progress value={workflowProgress} className="h-2" />
                                <span className="text-xs text-muted-foreground">{workflowProgress}%</span>
                            </div>
                        )}
                        
                        {cleanDetails?.metrics && (
                            <div className="text-xs text-muted-foreground">
                                {cleanDetails.metrics.success_count || 0} success,{' '}
                                {cleanDetails.metrics.error_count || 0} errors
                            </div>
                        )}
                        
                        {hasChildren && (
                            <div className="text-xs text-muted-foreground">
                                {log.children?.length} workers
                            </div>
                        )}
                        
                        {cleanDetails?.content_generated && (
                            <div className="text-xs text-blue-600">
                                {cleanDetails.content_generated.length} content generated
                            </div>
                        )}
                        
                        {cleanDetails?.content_published && (
                            <div className="text-xs text-green-600">
                                {cleanDetails.content_published.length} published
                            </div>
                        )}
                    </div>
                </TableCell>
                
                <TableCell>
                    <div className="text-sm">
                        {cleanDetails?.execution.duration_ms 
                            ? `${cleanDetails.execution.duration_ms}ms`
                            : formatExecutionTime(log.execution_time)
                        }
                    </div>
                </TableCell>
                
                <TableCell onClick={(e) => e.stopPropagation()}>
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="View clean structured data"
                            >
                                <Database className="w-4 h-4" />
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>Clean Log Data: {log.job_name}</DialogTitle>
                                <DialogDescription>
                                    Direct access to structured data - no parsing required
                                </DialogDescription>
                            </DialogHeader>
                            <CleanLogViewer log={log} />
                        </DialogContent>
                    </Dialog>
                </TableCell>
            </TableRow>

            {/* Expanded Details */}
            {isExpanded && (
                <TableRow>
                    <TableCell colSpan={6} className="bg-gray-50 p-0">
                        <div className="p-6">
                            <CleanLogDetails log={log} />
                        </div>
                    </TableCell>
                </TableRow>
            )}

            {/* Child Rows (Workers) */}
            {isExpanded && hasChildren && (
                <>
                    {log.children?.map((child) => (
                        <TableRow key={child.id || ''} className="bg-blue-50">
                            <TableCell className="pl-8">
                                <div className="w-4 h-4 border-l-2 border-b-2 border-gray-300"></div>
                            </TableCell>
                            <TableCell className="font-medium">
                                <div className="flex items-center space-x-2">
                                    {getStatusIcon(child.status || '')}
                                    <span className="text-sm">{child.job_name}</span>
                                    <Badge variant="secondary" className="text-xs">
                                        Worker
                                    </Badge>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge variant={getStatusBadgeVariant(child.status || '')} className="text-xs">
                                    {child.status || 'unknown'}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {isCleanCronLogDetails(child.details) && child.details.metrics && (
                                    <div className="text-xs text-muted-foreground">
                                        {child.details.metrics.items_processed || 0} processed
                                    </div>
                                )}
                            </TableCell>
                            <TableCell>
                                <div className="text-sm">
                                    {formatExecutionTime(child.execution_time)}
                                </div>
                            </TableCell>
                            <TableCell>
                                {child.error_message && (
                                    <div className="text-xs text-red-600 truncate" title={child.error_message}>
                                        {child.error_message}
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </>
            )}
        </>
    )
}

// Clean log details component - direct object access, no parsing!
function CleanLogDetails({ log }: { log: CleanCronJobLog }) {
    const cleanDetails = isCleanCronLogDetails(log.details) ? log.details : null
    
    if (!cleanDetails && !log.error_message) {
        return (
            <div className="text-center py-4 text-muted-foreground">
                No clean structured data available
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Error Message */}
            {(log.error_message || cleanDetails?.error) && (
                <Card className="border-red-200 bg-red-50">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-red-800 flex items-center space-x-2">
                            <AlertTriangle className="w-5 h-5" />
                            <span>Error Details</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {cleanDetails?.error && (
                            <div className="space-y-2">
                                <p className="text-red-700 font-medium">{cleanDetails.error.message}</p>
                                {cleanDetails.error.error_code && (
                                    <p className="text-sm text-red-600">
                                        Code: {cleanDetails.error.error_code}
                                    </p>
                                )}
                                {cleanDetails.error.context && (
                                    <pre className="text-xs text-red-600 bg-red-100 p-2 rounded">
                                        {JSON.stringify(cleanDetails.error.context, null, 2)}
                                    </pre>
                                )}
                            </div>
                        )}
                        {log.error_message && !cleanDetails?.error && (
                            <pre className="text-red-700 text-sm whitespace-pre-wrap">
                                {log.error_message}
                            </pre>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* File References */}
            <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center space-x-2 text-amber-800">
                        <BookOpen className="w-5 h-5" />
                        <span>{getJobFileReferences(log.job_name || '').label}</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {getJobFileReferences(log.job_name || '').files.map((file, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm">
                                <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                <code className="bg-amber-100 px-2 py-1 rounded text-amber-800 font-mono text-xs">
                                    {file}
                                </code>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Clean Execution Summary */}
            {cleanDetails && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="flex items-center space-x-2">
                            <Zap className="w-5 h-5" />
                            <span>Clean Execution Summary</span>
                            <Badge variant="default" className="text-xs">v2.0</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div>
                                <span className="font-medium text-gray-600">Status:</span>
                                <p className="mt-1">{cleanDetails.execution.status}</p>
                            </div>
                            
                            <div>
                                <span className="font-medium text-gray-600">Started:</span>
                                <p className="mt-1">
                                    {format(new Date(cleanDetails.execution.start_time), 'HH:mm:ss')}
                                </p>
                            </div>
                            
                            {cleanDetails.execution.end_time && (
                                <div>
                                    <span className="font-medium text-gray-600">Completed:</span>
                                    <p className="mt-1">
                                        {format(new Date(cleanDetails.execution.end_time), 'HH:mm:ss')}
                                    </p>
                                </div>
                            )}
                            
                            {cleanDetails.execution.duration_ms && (
                                <div>
                                    <span className="font-medium text-gray-600">Duration:</span>
                                    <p className="mt-1">{cleanDetails.execution.duration_ms}ms</p>
                                </div>
                            )}
                            
                            {cleanDetails.execution.trigger_type && (
                                <div>
                                    <span className="font-medium text-gray-600">Trigger:</span>
                                    <p className="mt-1 capitalize">{cleanDetails.execution.trigger_type}</p>
                                </div>
                            )}
                            
                            {cleanDetails.execution.triggered_by && (
                                <div>
                                    <span className="font-medium text-gray-600">Triggered By:</span>
                                    <p className="mt-1 font-mono text-xs">{cleanDetails.execution.triggered_by}</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {cleanDetails && (
                <Tabs defaultValue="workflow" className="w-full">
                    <TabsList className="grid w-full grid-cols-5">
                        <TabsTrigger value="workflow">
                            Workflow ({cleanDetails.workflow_steps?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="content">
                            Generated ({cleanDetails.content_generated?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="published">
                            Published ({cleanDetails.content_published?.length || 0})
                        </TabsTrigger>
                        <TabsTrigger value="metrics">
                            Metrics
                        </TabsTrigger>
                        <TabsTrigger value="raw">
                            Raw Data
                        </TabsTrigger>
                    </TabsList>

                    {/* Workflow Steps Tab */}
                    <TabsContent value="workflow" className="space-y-4">
                        {cleanDetails.workflow_steps && cleanDetails.workflow_steps.length > 0 ? (
                            <CleanWorkflowStepsTimeline steps={cleanDetails.workflow_steps} />
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No workflow steps recorded
                            </div>
                        )}
                    </TabsContent>

                    {/* Content Generated Tab */}
                    <TabsContent value="content" className="space-y-4">
                        {cleanDetails.content_generated && cleanDetails.content_generated.length > 0 ? (
                            <CleanContentGeneratedList items={cleanDetails.content_generated} />
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No content generated
                            </div>
                        )}
                    </TabsContent>

                    {/* Content Published Tab */}
                    <TabsContent value="published" className="space-y-4">
                        {cleanDetails.content_published && cleanDetails.content_published.length > 0 ? (
                            <CleanContentPublishedList items={cleanDetails.content_published} />
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No content published
                            </div>
                        )}
                    </TabsContent>

                    {/* Metrics Tab */}
                    <TabsContent value="metrics" className="space-y-4">
                        {cleanDetails.metrics ? (
                            <CleanMetricsDisplay metrics={cleanDetails.metrics} />
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No metrics recorded
                            </div>
                        )}
                    </TabsContent>

                    {/* Raw Data Tab */}
                    <TabsContent value="raw">
                        <CleanJsonFormatter 
                            data={cleanDetails} 
                            title="Clean Structured Data" 
                        />
                    </TabsContent>
                </Tabs>
            )}
        </div>
    )
}

// Clean workflow steps timeline - direct access!
function CleanWorkflowStepsTimeline({ steps }: { steps: CleanWorkflowStep[] }) {
    return (
        <div className="space-y-4">
            {steps.map((step, index) => (
                <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-600">
                            <CheckCircle className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                            <h4 className="font-medium">{step.step}</h4>
                            {step.status && (
                                <Badge variant={step.status === 'completed' ? 'default' : step.status === 'failed' ? 'destructive' : 'secondary'}>
                                    {step.status}
                                </Badge>
                            )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {format(new Date(step.timestamp), 'HH:mm:ss.SSS')}
                            {step.duration_ms && ` • ${step.duration_ms}ms`}
                        </p>
                        {step.result && Object.keys(step.result).length > 0 && (
                            <div className="mt-2">
                                <CleanJsonFormatter 
                                    data={step.result} 
                                    title="Step Result"
                                    collapsible
                                />
                            </div>
                        )}
                        {step.error_message && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                <p className="text-red-700 text-sm">{step.error_message}</p>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    )
}

// Clean content generated list - direct access!
function CleanContentGeneratedList({ items }: { items: CleanContentGenerated[] }) {
    return (
        <div className="grid gap-4">
            {items.map((item, index) => (
                <Card key={index}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                    <Link 
                                        to={`/content/${item.content_id}`}
                                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                    >
                                        {item.content_type}
                                    </Link>
                                    <Badge variant="outline">{item.content_id.slice(0, 8)}...</Badge>
                                </div>
                                {item.name && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {item.name}
                                    </p>
                                )}
                                {item.convergence_id && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Convergence: {item.convergence_id.slice(0, 8)}...
                                    </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                    Created: {format(new Date(item.created_at), 'MMM d, HH:mm')}
                                </p>
                            </div>
                            <div className="ml-2">
                                <Link 
                                    to={`/content/${item.content_id}`}
                                    className="text-gray-400 hover:text-blue-600 transition-colors"
                                    title="View content details"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    )
}

// Clean content published list - direct access!
function CleanContentPublishedList({ items }: { items: CleanContentPublished[] }) {
    return (
        <div className="grid gap-4">
            {items.map((item, index) => (
                <Card key={index}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                    <Share className="w-4 h-4 text-green-600" />
                                    <Link 
                                        to={`/brands/${item.brand_id}/connections`}
                                        className="font-medium text-green-600 hover:text-green-800 hover:underline transition-colors"
                                        title="View platform connections"
                                    >
                                        {item.platform}
                                    </Link>
                                    <Badge 
                                        variant={item.status === 'published' ? 'default' : 
                                                item.status === 'failed' ? 'destructive' : 'secondary'}
                                    >
                                        {item.status}
                                    </Badge>
                                </div>
                                {item.published_at && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Published: {format(new Date(item.published_at), 'MMM d, HH:mm')}
                                    </p>
                                )}
                                {item.scheduled_at && !item.published_at && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Scheduled: {format(new Date(item.scheduled_at), 'MMM d, HH:mm')}
                                    </p>
                                )}
                                {item.error_message && (
                                    <p className="text-sm text-red-600 mt-1">
                                        Error: {item.error_message}
                                    </p>
                                )}
                            </div>
                            <div className="ml-2">
                                <Link 
                                    to={`/brands/${item.brand_id}/connections`}
                                    className="text-gray-400 hover:text-green-600 transition-colors"
                                    title="View platform connections"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    )
}

// Clean metrics display - direct access!
function CleanMetricsDisplay({ metrics }: { metrics: CleanPerformanceMetrics }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {metrics.items_processed !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">{metrics.items_processed}</div>
                        <div className="text-sm text-blue-800">Items Processed</div>
                    </CardContent>
                </Card>
            )}
            
            {metrics.success_count !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{metrics.success_count}</div>
                        <div className="text-sm text-green-800">Successful</div>
                    </CardContent>
                </Card>
            )}
            
            {metrics.error_count !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-red-600">{metrics.error_count}</div>
                        <div className="text-sm text-red-800">Errors</div>
                    </CardContent>
                </Card>
            )}
            
            {metrics.content_generated !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-purple-600">{metrics.content_generated}</div>
                        <div className="text-sm text-purple-800">Content Generated</div>
                    </CardContent>
                </Card>
            )}
            
            {metrics.posts_published !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-indigo-600">{metrics.posts_published}</div>
                        <div className="text-sm text-indigo-800">Posts Published</div>
                    </CardContent>
                </Card>
            )}
            
            {metrics.emails_sent !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-orange-600">{metrics.emails_sent}</div>
                        <div className="text-sm text-orange-800">Emails Sent</div>
                    </CardContent>
                </Card>
            )}
            
            {metrics.api_calls !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-cyan-600">{metrics.api_calls}</div>
                        <div className="text-sm text-cyan-800">API Calls</div>
                    </CardContent>
                </Card>
            )}
            
            {metrics.credit_consumption !== undefined && (
                <Card>
                    <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-yellow-600">{metrics.credit_consumption}</div>
                        <div className="text-sm text-yellow-800">Credits Used</div>
                    </CardContent>
                </Card>
            )}
        </div>
    )
}

// Clean JSON formatter - no parsing required!
function CleanJsonFormatter({ 
    data, 
    title, 
    collapsible = false 
}: { 
    data: any
    title?: string
    collapsible?: boolean 
}) {
    const [isExpanded, setIsExpanded] = useState(!collapsible)
    const { toast } = useToast()
    const jsonString = JSON.stringify(data, null, 2)
    
    const copyToClipboard = () => {
        navigator.clipboard.writeText(jsonString)
        toast({
            title: "Copied to clipboard",
            description: "Clean structured data copied - no parsing required!",
        })
    }
    
    if (!data) {
        return (
            <div className="text-center py-4 text-muted-foreground">
                No data available
            </div>
        )
    }
    
    const content = (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                {title && <h5 className="font-medium text-sm">{title}</h5>}
                <div className="flex items-center space-x-2">
                    <Badge variant="default" className="text-xs">
                        Clean v2.0
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                        {jsonString.length} characters
                    </span>
                    <Button 
                        onClick={copyToClipboard}
                        variant="ghost" 
                        size="sm"
                        className="h-6 px-2"
                    >
                        <Copy className="w-3 h-3" />
                    </Button>
                </div>
            </div>
            <pre className="p-3 bg-gray-50 border rounded text-xs max-h-96 overflow-auto font-mono whitespace-pre-wrap">
                {jsonString}
            </pre>
        </div>
    )
    
    if (collapsible) {
        return (
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                <div className="flex items-center justify-between py-2">
                    <CollapsibleTrigger className="flex items-center space-x-2 text-sm hover:text-blue-600">
                        {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                        ) : (
                            <ChevronRight className="w-4 h-4" />
                        )}
                        <span>{title || 'View Clean JSON Data'}</span>
                    </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                    {content}
                </CollapsibleContent>
            </Collapsible>
        )
    }
    
    return content
}

// Clean log viewer for detailed inspection
function CleanLogViewer({ log }: { log: CleanCronJobLog }) {
    const cleanDetails = isCleanCronLogDetails(log.details) ? log.details : null
    const { toast } = useToast()
    
    const copyToClipboard = () => {
        navigator.clipboard.writeText(JSON.stringify(log, null, 2))
        toast({
            title: "Copied to clipboard",
            description: "Complete clean log data copied successfully",
        })
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    {cleanDetails && (
                        <Badge variant="default">
                            <Zap className="w-3 h-3 mr-1" />
                            Clean v{cleanDetails._meta?.schema_version || '2.0'}
                        </Badge>
                    )}
                    {!cleanDetails && (
                        <Badge variant="destructive">
                            Legacy Structure
                        </Badge>
                    )}
                </div>
                <Button onClick={copyToClipboard} variant="outline" size="sm">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy All
                </Button>
            </div>

            {cleanDetails ? (
                <Tabs defaultValue="overview">
                    <TabsList>
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="execution">Execution</TabsTrigger>
                        <TabsTrigger value="data">Clean Data</TabsTrigger>
                        <TabsTrigger value="raw">Raw JSON</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Job Name</label>
                                <p className="mt-1 text-sm">{log.job_name}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Status</label>
                                <p className="mt-1">
                                    <Badge variant={getStatusBadgeVariant(cleanDetails.execution.status)}>
                                        {cleanDetails.execution.status}
                                    </Badge>
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Schema Version</label>
                                <p className="mt-1">
                                    <Badge variant="default">
                                        <Zap className="w-3 h-3 mr-1" />
                                        Clean v{cleanDetails._meta?.schema_version || '2.0'}
                                    </Badge>
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Duration</label>
                                <p className="mt-1 text-sm">
                                    {cleanDetails.execution.duration_ms 
                                        ? `${cleanDetails.execution.duration_ms}ms`
                                        : 'N/A'
                                    }
                                </p>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="execution">
                        <CleanJsonFormatter 
                            data={cleanDetails.execution} 
                            title="Execution Info"
                        />
                    </TabsContent>

                    <TabsContent value="data">
                        <div className="space-y-4">
                            {cleanDetails.workflow_steps && (
                                <CleanJsonFormatter 
                                    data={cleanDetails.workflow_steps} 
                                    title="Workflow Steps"
                                    collapsible
                                />
                            )}
                            {cleanDetails.metrics && (
                                <CleanJsonFormatter 
                                    data={cleanDetails.metrics} 
                                    title="Performance Metrics"
                                    collapsible
                                />
                            )}
                            {cleanDetails.content_generated && (
                                <CleanJsonFormatter 
                                    data={cleanDetails.content_generated} 
                                    title="Content Generated"
                                    collapsible
                                />
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="raw">
                        <CleanJsonFormatter 
                            data={log} 
                            title="Complete Clean Log Data"
                        />
                    </TabsContent>
                </Tabs>
            ) : (
                <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Legacy Structure Detected</h3>
                    <p className="text-gray-500">
                        This log uses the legacy structure and may require parsing. 
                        Migrate to clean v2.0 structure for direct object access.
                    </p>
                    {log.details && (
                        <div className="mt-4">
                            <CleanJsonFormatter 
                                data={log.details} 
                                title="Legacy Log Data"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}